/**
 * Test Connection Log Window Component
 *
 * Displays the results of Plaid connection tests and API actions in a log-style panel.
 * Shows account information on success or error details on failure.
 * Supports both test connection results and Plaid API action results.
 *
 * @fileoverview Log window for displaying test connection and action results
 *
 * @example
 * <TestConnectionLogWindow
 *   result={testResult}
 *   actionResult={actionResult}
 *   isLoading={isTesting || isActionLoading}
 *   onClear={() => { setTestResult(null); setActionResult(null); }}
 * />
 */

import React from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Box,
  Badge,
  Icon,
  Button,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdError,
  MdClear,
  MdAccountBalance,
  MdAccessTime,
  MdPlayArrow,
} from 'react-icons/md';
import { ActionResult, ActionResultDisplay } from './ActionResultDisplay';

/**
 * Account information returned from test connection
 */
export interface TestConnectionAccount {
  /** Plaid's unique account identifier */
  accountId: string;
  /** Account name */
  name: string;
  /** Official name from the financial institution */
  officialName: string | null;
  /** Last 2-4 digits of account number (masked) */
  mask: string | null;
  /** Account type (e.g., depository, credit, loan, investment) */
  type: string;
  /** Account subtype (e.g., checking, savings, credit card) */
  subtype: string;
  /** Current balance (total funds or amount owed) */
  balanceCurrent: number | null;
  /** Available balance (funds available for withdrawal) */
  balanceAvailable: number | null;
  /** Credit limit or overdraft limit */
  balanceLimit: number | null;
  /** ISO-4217 currency code (e.g., USD) */
  currencyCode: string | null;
}

/**
 * Result of testing a Plaid connection
 */
export interface TestConnectionResult {
  /** Whether the connection test was successful */
  success: boolean;
  /** Number of accounts found */
  accountCount?: number;
  /** Account details */
  accounts?: TestConnectionAccount[];
  /** Institution name from the connection */
  institutionName?: string;
  /** Error message if connection failed */
  error?: string;
  /** Timestamp of the test */
  timestamp: string;
}

/**
 * Props for TestConnectionLogWindow
 */
interface TestConnectionLogWindowProps {
  /** Test connection result to display */
  result: TestConnectionResult | null;
  /** Action result to display */
  actionResult?: ActionResult | null;
  /** Whether a test or action is currently in progress */
  isLoading: boolean;
  /** Callback to clear the test result */
  onClear: () => void;
  /** Callback to clear the action result */
  onClearAction?: () => void;
  /** Loading text to display */
  loadingText?: string;
}

/**
 * Format currency value for display
 *
 * @param value - Numeric value to format
 * @param currencyCode - ISO currency code (defaults to USD)
 * @returns Formatted currency string
 */
function formatCurrency(value: number | null, currencyCode?: string | null): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode || 'USD',
  }).format(value);
}

/**
 * Format timestamp for display
 *
 * @param timestamp - ISO timestamp string
 * @returns Formatted time string
 */
function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return 'Unknown';
  }
}

/**
 * Loading state component props
 */
interface LoadingStateProps {
  /** Custom loading text to display */
  text?: string;
}

/**
 * Loading state component
 */
const LoadingState: React.FC<LoadingStateProps> = ({ text = 'Testing connection...' }) => (
  <VStack spacing={4} py={8}>
    <Spinner size="lg" color="accent.primary" thickness="3px" />
    <Text color="text.secondary" fontSize="sm">
      {text}
    </Text>
  </VStack>
);

/**
 * Empty state component
 */
const EmptyState: React.FC = () => (
  <VStack spacing={2} py={6}>
    <Icon as={MdAccountBalance} color="text.muted" boxSize={10} />
    <Text color="text.muted" fontSize="sm" textAlign="center">
      Click "Test Connection" to verify your bank connection
    </Text>
  </VStack>
);

/**
 * Success result display
 */
interface SuccessResultProps {
  result: TestConnectionResult;
}

const SuccessResult: React.FC<SuccessResultProps> = ({ result }) => (
  <VStack align="stretch" spacing={4}>
    {/* Success header */}
    <HStack justify="space-between">
      <HStack spacing={2}>
        <Icon as={MdCheckCircle} color="green.400" boxSize={5} />
        <Text color="green.400" fontWeight="semibold">
          Connection Successful
        </Text>
      </HStack>
      <Badge colorScheme="green" px={2}>
        {result.accountCount} {result.accountCount === 1 ? 'Account' : 'Accounts'}
      </Badge>
    </HStack>

    {/* Institution name */}
    {result.institutionName && (
      <HStack spacing={2}>
        <Icon as={MdAccountBalance} color="accent.primary" boxSize={4} />
        <Text color="text.secondary" fontSize="sm">
          {result.institutionName}
        </Text>
      </HStack>
    )}

    {/* Accounts - detailed cards */}
    {result.accounts && result.accounts.length > 0 && (
      <VStack align="stretch" spacing={3}>
        {result.accounts.map((account, index) => (
          <Box
            key={account.accountId || index}
            bg="bg.primary"
            borderRadius="md"
            p={3}
            borderLeft="3px solid"
            borderLeftColor="xacto.electricCyan"
          >
            {/* Account header with name and mask */}
            <HStack justify="space-between" mb={2}>
              <VStack align="start" spacing={0}>
                <Text color="text.primary" fontSize="sm" fontWeight="medium">
                  {account.name}
                </Text>
                {account.officialName && account.officialName !== account.name && (
                  <Text color="text.muted" fontSize="xs">
                    {account.officialName}
                  </Text>
                )}
              </VStack>
              <HStack spacing={2}>
                {account.mask && (
                  <Code
                    bg="bg.muted"
                    color="text.secondary"
                    fontSize="xs"
                    px={2}
                    py={0.5}
                    borderRadius="sm"
                  >
                    ****{account.mask}
                  </Code>
                )}
                <Badge colorScheme="cyan" variant="subtle" fontSize="2xs">
                  {account.subtype || account.type}
                </Badge>
              </HStack>
            </HStack>

            {/* Balance information */}
            <HStack spacing={4} wrap="wrap">
              {/* Current Balance */}
              <VStack align="start" spacing={0}>
                <Text color="text.muted" fontSize="2xs" textTransform="uppercase">
                  Current
                </Text>
                <Text color="text.primary" fontSize="sm" fontFamily="mono" fontWeight="medium">
                  {formatCurrency(account.balanceCurrent, account.currencyCode)}
                </Text>
              </VStack>

              {/* Available Balance - show if different from current */}
              {account.balanceAvailable !== null && account.balanceAvailable !== account.balanceCurrent && (
                <VStack align="start" spacing={0}>
                  <Text color="text.muted" fontSize="2xs" textTransform="uppercase">
                    Available
                  </Text>
                  <Text color="green.300" fontSize="sm" fontFamily="mono">
                    {formatCurrency(account.balanceAvailable, account.currencyCode)}
                  </Text>
                </VStack>
              )}

              {/* Credit Limit - show for credit accounts */}
              {account.balanceLimit !== null && (
                <VStack align="start" spacing={0}>
                  <Text color="text.muted" fontSize="2xs" textTransform="uppercase">
                    {account.type === 'credit' ? 'Credit Limit' : 'Limit'}
                  </Text>
                  <Text color="text.secondary" fontSize="sm" fontFamily="mono">
                    {formatCurrency(account.balanceLimit, account.currencyCode)}
                  </Text>
                </VStack>
              )}
            </HStack>

            {/* Account ID for reference */}
            {account.accountId && (
              <Text color="text.muted" fontSize="2xs" mt={2}>
                ID: {account.accountId.substring(0, 8)}...
              </Text>
            )}
          </Box>
        ))}
      </VStack>
    )}

    {/* Timestamp */}
    <HStack spacing={2}>
      <Icon as={MdAccessTime} color="text.muted" boxSize={3} />
      <Text color="text.muted" fontSize="xs">
        Tested at {formatTimestamp(result.timestamp)}
      </Text>
    </HStack>
  </VStack>
);

/**
 * Error result display
 */
interface ErrorResultProps {
  result: TestConnectionResult;
}

const ErrorResult: React.FC<ErrorResultProps> = ({ result }) => {
  // Parse error code and message
  const errorParts = result.error?.split(': ') || ['UNKNOWN_ERROR', 'An error occurred'];
  const errorCode = errorParts[0];
  const errorMessage = errorParts.slice(1).join(': ') || errorParts[0];

  // Determine if this is a recoverable error
  const isLoginRequired = errorCode === 'ITEM_LOGIN_REQUIRED';

  return (
    <VStack align="stretch" spacing={4}>
      {/* Error header */}
      <HStack spacing={2}>
        <Icon as={MdError} color="red.400" boxSize={5} />
        <Text color="red.400" fontWeight="semibold">
          Connection Failed
        </Text>
      </HStack>

      {/* Error details */}
      <Alert
        status={isLoginRequired ? 'warning' : 'error'}
        bg={isLoginRequired ? 'yellow.900' : 'red.900'}
        borderRadius="md"
        flexDirection="column"
        alignItems="flex-start"
      >
        <HStack spacing={2} mb={2}>
          <AlertIcon />
          <Badge
            colorScheme={isLoginRequired ? 'yellow' : 'red'}
            fontFamily="mono"
            fontSize="xs"
          >
            {errorCode}
          </Badge>
        </HStack>
        <Text
          color={isLoginRequired ? 'yellow.200' : 'red.200'}
          fontSize="sm"
        >
          {errorMessage}
        </Text>
        {isLoginRequired && (
          <Text color="yellow.300" fontSize="xs" mt={2}>
            Use the "Re-authenticate" button to update your bank credentials.
          </Text>
        )}
      </Alert>

      {/* Error code for debugging */}
      <Box>
        <Text color="text.muted" fontSize="xs" mb={1}>
          Error Details
        </Text>
        <Code
          bg="bg.primary"
          color="text.secondary"
          p={2}
          borderRadius="md"
          fontSize="xs"
          display="block"
          whiteSpace="pre-wrap"
        >
          {result.error}
        </Code>
      </Box>

      {/* Timestamp */}
      <HStack spacing={2}>
        <Icon as={MdAccessTime} color="text.muted" boxSize={3} />
        <Text color="text.muted" fontSize="xs">
          Tested at {formatTimestamp(result.timestamp)}
        </Text>
      </HStack>
    </VStack>
  );
};

/**
 * Test Connection Log Window Component
 *
 * Displays test connection results in a log-style format.
 * Shows:
 * - Loading spinner while test is in progress
 * - Success message with account details on successful test
 * - Error message with details on failed test
 * - Empty state when no test has been run
 *
 * @param props - Component props
 * @returns Test connection log window component
 */
export const TestConnectionLogWindow: React.FC<TestConnectionLogWindowProps> = ({
  result,
  actionResult,
  isLoading,
  onClear,
  onClearAction,
  loadingText,
}) => {
  // Determine if there's any content to display
  const hasContent = result || actionResult;
  return (
    <Card
      bg="bg.surface"
      borderColor="xacto.mutedNavy"
      borderWidth="1px"
    >
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <Heading size="sm" color="text.primary">
            Connection Test Log
          </Heading>
          {hasContent && !isLoading && (
            <Button
              size="xs"
              variant="ghost"
              color="text.muted"
              leftIcon={<Icon as={MdClear} />}
              onClick={() => {
                onClear();
                onClearAction?.();
              }}
              _hover={{ color: 'text.secondary', bg: 'bg.muted' }}
            >
              Clear All
            </Button>
          )}
        </HStack>
      </CardHeader>

      <CardBody pt={2}>
        {isLoading ? (
          <LoadingState text={loadingText} />
        ) : hasContent ? (
          <VStack align="stretch" spacing={6}>
            {/* Test connection result */}
            {result && (
              result.success ? (
                <SuccessResult result={result} />
              ) : (
                <ErrorResult result={result} />
              )
            )}

            {/* Divider between results if both exist */}
            {result && actionResult && (
              <Box borderTop="1px solid" borderColor="xacto.mutedNavy" pt={4} />
            )}

            {/* Action result */}
            {actionResult && onClearAction && (
              <ActionResultDisplay result={actionResult} onClear={onClearAction} />
            )}
          </VStack>
        ) : (
          <EmptyState />
        )}
      </CardBody>
    </Card>
  );
};

export default TestConnectionLogWindow;
